/**
 * MRT-Klassifizierer: ordnet die freitextlichen Buchungs-Bezeichnungen der
 * Praxen (aus den Online-Terminportalen) einem festen Satz an Körperregionen /
 * Untersuchungstypen zu, die wir als Suchoptionen anbieten.
 *
 * Designentscheidungen:
 *   - MULTI-LABEL: Eine Bezeichnung kann mehreren Regionen zugeordnet werden
 *     (z. B. "MRT HWS + BWS" -> {hws, bws}, "MRT Sprunggelenk / Fuß" ->
 *     {sprunggelenk, fuss}). `classifyRegions` liefert daher ein (sortiertes)
 *     Array von Regionen.
 *   - SPEZIFISCHE KÖRPERTEILE als Granularität (Knie, Schulter, LWS, …), nicht
 *     grobe Gruppen. Die grobe Gruppe steht zusätzlich in `REGION_GROUP` für die
 *     UI-Gruppierung zur Verfügung.
 *   - SONDERTYPEN (Angiographie, Prostata, Mamma, Herz, MRCP …) sind eigene
 *     "Regionen", weil Nutzer gezielt danach suchen.
 *
 * Bewusst IGNORIERT (orthogonale Achsen, keine eigene Region):
 *   - Kontrastmittel (mit/ohne KM, nativ)  -> siehe `extractKontrastmittel`
 *   - Gewichtsgrenzen ("bis 160 KG", "ab 100 kg")
 *   - offenes/geschlossenes MRT, Gelenkscanner
 *   - Seite (links/rechts/beidseits), Sedierung/Beruhigung
 *   - Kostenträger (privat, IGeL, BG, Selbstzahler, …)
 */

/* ──────────────────────────────────────────────────────────────────
 * 1) Regionen (= Suchoptionen / Fixture-Dateinamen)
 * ────────────────────────────────────────────────────────────────── */

export type RegionGroup =
	| 'Kopf & Neuro'
	| 'Hals'
	| 'Wirbelsäule'
	| 'Brustkorb / Thorax'
	| 'Bauchraum & Organe'
	| 'Becken'
	| 'Gefäße'
	| 'Obere Extremität'
	| 'Untere Extremität'
	| 'Spezial';

/** Anzeige-Label + Gruppe je Region. Reihenfolge = grobe anatomische Sortierung. */
export const REGION_META = {
	// ── Kopf & Neuro ──────────────────────────────────────────────
	kopf: { label: 'Kopf / Schädel', group: 'Kopf & Neuro' },
	hypophyse: { label: 'Hypophyse / Sella', group: 'Kopf & Neuro' },
	orbita: { label: 'Orbita / Augenhöhle', group: 'Kopf & Neuro' },
	nnh: { label: 'Nasennebenhöhlen', group: 'Kopf & Neuro' },
	felsenbein: { label: 'Felsenbein / Innenohr', group: 'Kopf & Neuro' },
	kiefergelenk: { label: 'Kiefergelenk', group: 'Kopf & Neuro' },
	myelon: { label: 'Myelon / Rückenmark', group: 'Kopf & Neuro' },

	// ── Hals ──────────────────────────────────────────────────────
	hals: { label: 'Hals / Halsweichteile', group: 'Hals' },

	// ── Wirbelsäule ───────────────────────────────────────────────
	hws: { label: 'Halswirbelsäule (HWS)', group: 'Wirbelsäule' },
	bws: { label: 'Brustwirbelsäule (BWS)', group: 'Wirbelsäule' },
	lws: { label: 'Lendenwirbelsäule (LWS)', group: 'Wirbelsäule' },
	wirbelsaeule: { label: 'Gesamte Wirbelsäule', group: 'Wirbelsäule' },
	isg: { label: 'ISG / Iliosakralgelenk', group: 'Wirbelsäule' },
	sakrum: { label: 'Kreuz- / Steißbein', group: 'Wirbelsäule' },

	// ── Brustkorb / Thorax ────────────────────────────────────────
	thorax: { label: 'Thorax / Lunge', group: 'Brustkorb / Thorax' },
	sternum: { label: 'Sternum / Brustbein', group: 'Brustkorb / Thorax' },
	sternoclaviculargelenk: { label: 'Sternoclaviculargelenk', group: 'Brustkorb / Thorax' },
	clavicula: { label: 'Clavicula / Schlüsselbein', group: 'Brustkorb / Thorax' },
	scapula: { label: 'Scapula / Schulterblatt', group: 'Brustkorb / Thorax' },
	axilla: { label: 'Axilla / Achselhöhle', group: 'Brustkorb / Thorax' },
	plexus: { label: 'Plexus / Armgeflecht', group: 'Brustkorb / Thorax' },
	mamma: { label: 'Mamma / Brust', group: 'Brustkorb / Thorax' },
	herz: { label: 'Herz / Cardio', group: 'Brustkorb / Thorax' },

	// ── Bauchraum & Organe ────────────────────────────────────────
	abdomen: { label: 'Abdomen / Oberbauch', group: 'Bauchraum & Organe' },
	leber: { label: 'Leber', group: 'Bauchraum & Organe' },
	niere: { label: 'Niere / Harnwege', group: 'Bauchraum & Organe' },
	pankreas: { label: 'Pankreas', group: 'Bauchraum & Organe' },
	mrcp: { label: 'MRCP / Gallenwege', group: 'Bauchraum & Organe' },
	darm: { label: 'Darm / Sellink', group: 'Bauchraum & Organe' },

	// ── Becken ────────────────────────────────────────────────────
	becken: { label: 'Becken', group: 'Becken' },
	prostata: { label: 'Prostata', group: 'Becken' },
	becken_weiblich: { label: 'Weibliches Becken (gyn.)', group: 'Becken' },
	hoden: { label: 'Hoden / Penis', group: 'Becken' },

	// ── Gefäße ────────────────────────────────────────────────────
	angiographie: { label: 'Angiographie / Gefäße', group: 'Gefäße' },

	// ── Obere Extremität ──────────────────────────────────────────
	schulter: { label: 'Schulter', group: 'Obere Extremität' },
	oberarm: { label: 'Oberarm', group: 'Obere Extremität' },
	ellenbogen: { label: 'Ellenbogen', group: 'Obere Extremität' },
	unterarm: { label: 'Unterarm', group: 'Obere Extremität' },
	handgelenk: { label: 'Handgelenk', group: 'Obere Extremität' },
	hand: { label: 'Hand', group: 'Obere Extremität' },
	finger: { label: 'Finger / Daumen', group: 'Obere Extremität' },
	arm: { label: 'Arm (allgemein)', group: 'Obere Extremität' },

	// ── Untere Extremität ─────────────────────────────────────────
	huefte: { label: 'Hüfte', group: 'Untere Extremität' },
	oberschenkel: { label: 'Oberschenkel', group: 'Untere Extremität' },
	knie: { label: 'Knie', group: 'Untere Extremität' },
	unterschenkel: { label: 'Unterschenkel', group: 'Untere Extremität' },
	sprunggelenk: { label: 'Sprunggelenk (OSG)', group: 'Untere Extremität' },
	achillessehne: { label: 'Achillessehne', group: 'Untere Extremität' },
	fuss: { label: 'Fuß', group: 'Untere Extremität' },
	bein: { label: 'Bein (allgemein)', group: 'Untere Extremität' },
	beinachse: { label: 'Beinachse / Rotation', group: 'Untere Extremität' },

	// ── Spezial ───────────────────────────────────────────────────
	ganzkoerper: { label: 'Ganzkörper', group: 'Spezial' },
	neurographie: { label: 'Neurographie (Nerven)', group: 'Spezial' }
} as const satisfies Record<string, { label: string; group: RegionGroup }>;

export type Region = keyof typeof REGION_META;

export const REGIONS = Object.keys(REGION_META) as Region[];

export const REGION_GROUP = (region: Region): RegionGroup => REGION_META[region].group;
export const REGION_LABEL = (region: Region): string => REGION_META[region].label;

/** Prüft (und verengt), ob ein beliebiger String eine bekannte Region ist. */
export function isRegion(value: string | null | undefined): value is Region {
	return value != null && Object.prototype.hasOwnProperty.call(REGION_META, value);
}

/* ──────────────────────────────────────────────────────────────────
 * 2) Aliase je Region (vor Normalisierung, natürlich geschrieben)
 *
 * Matching erfolgt TOKEN-basiert (ganze Wörter), d. h. "hand" matcht NICHT
 * innerhalb von "handgelenk". Mehrwort-Aliase ("obere extremität") werden als
 * zusammenhängende Tokenfolge gesucht.
 * ────────────────────────────────────────────────────────────────── */

const ALIASES: Record<Region, string[]> = {
	kopf: [
		'Kopf',
		'Schädel',
		'Schädelbasis',
		'Gesichtsschädel',
		'Neurocranium',
		'Neurokranium',
		'cMRT',
		'Cerebrum',
		'Cerebral',
		'Gehirn',
		'Hirnvolumetrie',
		'Neuroachse',
		'Neuronachse'
	],
	hypophyse: ['Hypophyse', 'Sella', 'Hirnanhangsdrüse', 'Hirnanhangdrüse'],
	orbita: ['Orbita', 'Orbitae', 'Augenhöhle', 'Sehnerv'],
	nnh: ['Nasennebenhöhlen', 'NNH', 'Mittelgesicht', 'Gesichtsschädel'],
	felsenbein: [
		'Felsenbein',
		'Innenohr',
		'Kleinhirnbrückenwinkel',
		'KHBW',
		'KBW',
		'Akustikusneurinom',
		'AKN'
	],
	kiefergelenk: ['Kiefergelenk', 'Kiefergelenke', 'Kiefer', 'TMG', 'Temporomandibulargelenk'],
	myelon: ['Myelon', 'Rückenmark', 'Neuroachse', 'Neuronachse'],

	hals: [
		'Hals',
		'Halsweichteile',
		'Halsweichteil',
		'HWT',
		'Parotis',
		'Speicheldrüse',
		'Speicheldrüsen',
		'Ohrspeicheldrüse',
		'Mundboden',
		'Schilddrüse'
	],

	hws: ['HWS', 'Halswirbelsäule', 'zervikale'],
	bws: ['BWS', 'Brustwirbelsäule'],
	lws: ['LWS', 'Lendenwirbelsäule', 'lumbale', 'lumbosakrale'],
	wirbelsaeule: [
		'Wirbelsäule',
		'Ganzwirbelsäule',
		'WS',
		'Mobitrack',
		'Total Spine',
		'Totalspine',
		'Wholespine',
		'Rücken'
	],
	isg: [
		'ISG',
		'ISF',
		'SIG',
		'Iliosakralgelenk',
		'Iliosakralgelenke',
		'Iliosacralgelenk',
		'Iliosacralgelenke',
		'Illiosakralgelenk',
		'Illiosakralgelenke',
		'Iliosakralfugen',
		'Ileosakralgelenk',
		'Ileosakralfugen',
		'lliosakralgelenk',
		'Sakroiliacalgelenk'
	],
	sakrum: ['Kreuzbein', 'Sacrum', 'Sakrum', 'Steißbein', 'Steiß', 'Os coccygis'],

	thorax: ['Thorax', 'Brustkorb', 'Lunge', 'Pulmo', 'Thoraxwand'],
	sternum: ['Sternum', 'Brustbein'],
	sternoclaviculargelenk: [
		'Sternoclaviculargelenk',
		'Sternoclaviculargelenke',
		'Sternoclavicular',
		'Sternoclavikular',
		'Sternoklavikulargelenke',
		'Sternoclavikulargelenk',
		'SternoClavicularGelenke',
		'SCG',
		'SC Gelenk',
		'Sterno Clavicular',
		'Sterno Clavicular Gelenk'
	],
	clavicula: ['Clavicula', 'Clavikula', 'Klavikula', 'Schlüsselbein'],
	scapula: ['Scapula', 'Schulterblatt'],
	axilla: ['Axilla', 'Achselhöhle', 'Achsel'],
	plexus: [
		'Plexus',
		'Armplexus',
		'Armgeflecht',
		'Thoraxapertur',
		'TOS',
		'Thoracic-outlet',
		'Thoracic Outlet'
	],
	mamma: ['Mamma', 'Mammae', 'Mammographie', 'Mammografie', 'Brust'],
	herz: ['Herz', 'Cardio', 'Kardio', 'CMR', 'Cardio'],

	abdomen: ['Abdomen', 'Bauch', 'Bauchraum', 'Oberbauch'],
	leber: ['Leber', 'Hepar', 'Primovist'],
	niere: [
		'Niere',
		'Nieren',
		'Nebenniere',
		'Nebennieren',
		'Urographie',
		'Urogramm',
		'Urografie',
		'Harnwege',
		'Blase',
		'ivp'
	],
	pankreas: ['Pankreas', 'Pancreas', 'Bauchspeicheldrüse'],
	mrcp: ['MRCP', 'Galle', 'Gallenwege', 'Cholangiographie', 'Cholangiopankreatikographie'],
	darm: [
		'Darm',
		'Dünndarm',
		'Sellink',
		'Enterographie',
		'Enterografie',
		'Kolonographie',
		'Kolonografie',
		'Hydro',
		'Defäkographie',
		'Defäkografie',
		'Defäko',
		'Rektum',
		'Enddarm',
		'Fistel'
	],

	becken: [
		'Becken',
		'Pelvis',
		'Pelvimetrie',
		'Beckenskelett',
		'Beckens',
		'Beckenweichteile',
		'Beckenboden',
		'Unterbauch',
		'Leiste'
	],
	prostata: ['Prostata', 'mpMRT'],
	becken_weiblich: [
		'gynäkologisch',
		'gynäkologische',
		'gyn',
		'Uterus',
		'Gebärmutter',
		'Adnexe',
		// "weiblich" ist für sich kein Körperteil ("MRT Mammographie (weibliche
		// Brust)") – nur zusammen mit Becken ist die gyn. Untersuchung gemeint.
		'weibliches Becken',
		'weibliche Becken',
		'Becken weiblich'
	],
	hoden: ['Hoden', 'Penis', 'Scrotum', 'Skrotum', 'Testis'],

	angiographie: [
		'Angiographie',
		'Angiografie',
		'Angio',
		'MRA',
		'TOF',
		'Carotis',
		'Carotiden',
		'Aorta',
		'Bauchaorta',
		'Subclavia',
		'Pulmonalis',
		'Phlebographie',
		'Venographie',
		'BBA',
		'ABB',
		'Gefäße',
		'Gefäßdarstellung',
		'supraaortale',
		'Nierenarterien',
		'Nierengefäße',
		'Halsgefäße',
		'Beingefäße',
		'Hirnarterien',
		'Hirnbasisarterien',
		'Halsarterien',
		'Nierengefäße'
	],

	schulter: ['Schulter', 'Schultergelenk'],
	oberarm: ['Oberarm', 'Humerus'],
	ellenbogen: ['Ellenbogen', 'Ellbogen', 'Ellenbogengelenk', 'EBG'],
	unterarm: ['Unterarm'],
	handgelenk: ['Handgelenk', 'Karpus', 'Carpus'],
	hand: ['Hand', 'Mittelhand', 'Manus'],
	finger: ['Finger', 'Daumen', 'Fingergelenk'],
	arm: ['Arm', 'obere Extremität', 'obere Extremitäten', 'Extremitäten'],

	huefte: ['Hüfte', 'Hüftgelenk', 'Hüftgelenke', 'Hüften', 'Coxa'],
	oberschenkel: ['Oberschenkel', 'Femur'],
	knie: ['Knie', 'Kniegelenk', 'Genu'],
	unterschenkel: ['Unterschenkel', 'Tibia', 'Wade', 'Schienbein'],
	sprunggelenk: ['Sprunggelenk', 'OSG', 'USG'],
	achillessehne: ['Achillessehne', 'Achilles'],
	fuss: [
		'Fuß',
		'Vorfuß',
		'Mittelfuß',
		'Pes',
		'Ferse',
		'Calcaneus',
		'Zehen',
		'Zeh',
		'Fußgelenk',
		'Morton'
	],
	bein: ['Bein', 'untere Extremität', 'untere Extremitäten', 'Extremitäten'],
	beinachse: [
		'Beinachse',
		'Rotationsmessung',
		'Rotationsmesssung',
		'Torsionswinkel',
		'Torsion',
		'Beinwinkelmessung',
		'Rotation',
		'Beinrotation',
		'Torsionsmessung',
		'Beinlänge'
	],

	ganzkoerper: ['Ganzkörper', 'Whole-Body', 'Wholebody'],
	neurographie: ['Neurographie', 'Neurografie', 'Nerven']
};

/* ──────────────────────────────────────────────────────────────────
 * 3) Normalisierung & Helfer
 * ────────────────────────────────────────────────────────────────── */

/** Normalisiert beliebige Bezeichnungen für robusten Vergleich (Token-Basis). */
export function normalize(input: string): string {
	return input
		.toLowerCase()
		.replace(/ä/g, 'ae')
		.replace(/ö/g, 'oe')
		.replace(/ü/g, 'ue')
		.replace(/ß/g, 'ss')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.replace(/\s+/g, ' ');
}

/** Holt das Buchungs-Label (rechts von "->>"), sonst die ganze Zeile. */
export function extractLabel(line: string): string {
	const idx = line.lastIndexOf('->>');
	const label = idx >= 0 ? line.slice(idx + 3) : line;
	return label.trim().replace(/[,;]+$/, '');
}

export type Kontrastmittel = 'mit' | 'ohne' | 'unbekannt';

const KM_MIT = ['mit km', 'mit kontrastmittel', 'mit kontrast', 'kontrastverstaerkt'];
const KM_OHNE = ['ohne km', 'ohne kontrastmittel', 'ohne kontrast', 'nativ'];

/** Liest die (orthogonale) Kontrastmittel-Achse aus einer normalisierten Bezeichnung. */
export function extractKontrastmittel(normalized: string): Kontrastmittel {
	for (const m of KM_OHNE) if (normalized.includes(m)) return 'ohne';
	for (const m of KM_MIT) if (normalized.includes(m)) return 'mit';
	return 'unbekannt';
}

/* ── Token-Index aus Aliasen aufbauen ──────────────────────────────── */

type AliasEntry = { region: Region; tokens: string[] };

const ALIAS_ENTRIES: AliasEntry[] = (() => {
	const out: AliasEntry[] = [];
	for (const region of REGIONS) {
		for (const alias of ALIASES[region]) {
			const tokens = normalize(alias).split(' ').filter(Boolean);
			if (tokens.length) out.push({ region, tokens });
		}
	}
	return out;
})();

/** Alle Tokens, die in irgendeinem Alias vorkommen (für Negations-Erkennung). */
const ALIAS_TOKENS: Set<string> = new Set(ALIAS_ENTRIES.flatMap((e) => e.tokens));

/** Deutsche Beugungs-Endungen, längste zuerst (Genitiv, Plural, Dativ). */
const INFLECTION_SUFFIXES = ['ens', 'es', 'en', 'e', 'n', 's'];

/**
 * Führt gebeugte Formen auf die Alias-Grundform zurück ("MRT des Schädels" →
 * schaedel, "MRT d. Kniegelenkes" → kniegelenk, "der Felsenbeine" → felsenbein).
 *
 * Bewusst konservativ: eine Endung wird nur abgeschnitten, wenn der Stamm ein
 * BEKANNTES Alias-Token ist. Dadurch bleiben Füllwörter ("keine", "Aufnahmen",
 * "Kosten") unangetastet und können keine Region auslösen.
 */
function lemmatize(token: string): string {
	if (ALIAS_TOKENS.has(token)) return token;
	for (const suffix of INFLECTION_SUFFIXES) {
		if (!token.endsWith(suffix)) continue;
		const stem = token.slice(0, -suffix.length);
		if (stem.length >= 3 && ALIAS_TOKENS.has(stem)) return stem;
	}
	return token;
}

/**
 * "Weiche" Territorien: Regionen, die SOWOHL eigenständige Organ-Untersuchungen
 * als auch reine Gefäßstudien benennen können. Sie werden nur dann unterdrückt,
 * wenn das Region-Wort DIREKT an einem "…angio…"-Token klebt
 * ("Angio Abdomen", "Schädel-Angio") – nicht bei echten Kombis ("Schädel mit
 * Angio", "Thorax / Aorta").
 */
const SOFT_TERRITORY = new Set(['kopf', 'schaedel', 'abdomen', 'bauch', 'thorax']);

/**
 * Artikel/Genitiv-Füllwörter, die beim Nachbarschafts-Check übersprungen werden:
 * "Angiographie des Abdomens" meint dasselbe wie "Angio Abdomen".
 */
const CONNECTOR_TOKENS = new Set([
	'der',
	'die',
	'das',
	'des',
	'dem',
	'den',
	'd',
	'von',
	'vom',
	'im',
	'am',
	'zur',
	'zum'
]);

/** Gefäß-"Territorien": bei Angio-Kontext kein eigener Körperregion-Treffer. */
const VASCULAR_TERRITORY = new Set([
	'hals',
	'carotis',
	'carotiden',
	'niere',
	'nieren',
	'aorta',
	'bauchaorta',
	'becken',
	'bein',
	'subclavia',
	'pulmonalis',
	'supraaortale',
	'nierenarterien',
	'nierengefaesse',
	'halsgefaesse',
	'halsarterien',
	'beingefaesse',
	'hirnarterien',
	'hirnbasisarterien'
]);

const NEGATION_MARKERS = new Set(['nicht', 'kein', 'keine', 'keinen', 'keiner', 'ausser']);

// "angio" als Teilstring deckt Komposita ab (Halsangio, Beckenbeinangio, …).
const ANGIO_RE = /angio|\btof\b|\bmra\b|\bbba\b|\babb\b|carotis|carotiden|\baorta\b|bauchaorta|phlebograf|venograf|subclavia|pulmonalis|gefaess|nierenarterien|halsgefaess|halsarterien|beingefaess|supraaortal/;

/* ──────────────────────────────────────────────────────────────────
 * 4) Vorverarbeitung: Spinal-Abkürzungen & Gewichts-/Gender-Rauschen
 * ────────────────────────────────────────────────────────────────── */

/**
 * Spaltet Abkürzungs-Kombis ("H/BWS", "Hals- und Brustwirbelsäule") so auf, dass
 * das nackte "Hals"/"Brust" nicht fälschlich als Halsweichteile/Mamma zählt.
 * Wirkt auf den ROH-Label-Text (vor `normalize`).
 */
function expandSpineAbbreviations(label: string): string {
	return (
		label
			.replace(/\bH\s*\/\s*B\s*\/\s*LWS\b/gi, 'HWS BWS LWS')
			.replace(/\bH\s*\/\s*BWS\b/gi, 'HWS BWS')
			.replace(/\bB\s*\/\s*LWS\b/gi, 'BWS LWS')
			// "Hals- und/oder Brustwirbelsäule", "Hals-/Brustwirbelsäule"
			.replace(/\bHals[-\s]*(?:und|oder|\/|\+)?[-\s]*Brustwirbel\w*/gi, 'HWS BWS')
			// "Brust- und/oder Lendenwirbelsäule"
			.replace(/\bBrust[-\s]*(?:und|oder|\/|\+)?[-\s]*Lendenwirbel\w*/gi, 'BWS LWS')
	);
}

/** Entfernt Gewichts-/Gender-Qualifier ("weiblich über 90 kg / männlich …"). */
function stripWeightGender(normalized: string): string {
	return normalized
		.replace(/(weiblich|maennlich)[a-z0-9 ]*?\d+\s*kg/g, ' ')
		.replace(/(ueber|unter|bis|ab)?\s*\d+\s*kg/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

/* ──────────────────────────────────────────────────────────────────
 * 5) Klassifizierung
 * ────────────────────────────────────────────────────────────────── */

/** Prüft, ob `needle` (Tokenfolge) als zusammenhängende Teilfolge in `hay` steckt. */
function containsSequence(hay: string[], needle: string[]): boolean {
	if (needle.length === 1) return hay.includes(needle[0]);
	outer: for (let i = 0; i + needle.length <= hay.length; i++) {
		for (let j = 0; j < needle.length; j++) {
			if (hay[i + j] !== needle[j]) continue outer;
		}
		return true;
	}
	return false;
}

/** Nächster inhaltstragender Nachbar in Richtung `step` (Artikel werden übersprungen). */
function neighbour(tokens: string[], index: number, step: -1 | 1): string {
	for (let i = index + step; i >= 0 && i < tokens.length; i += step) {
		if (!CONNECTOR_TOKENS.has(tokens[i])) return tokens[i];
	}
	return '';
}

/** Entfernt negierte Region-Tokens ("… NICHT Halsweichteile", "kein Prostata"). */
function applyNegations(tokens: string[]): string[] {
	const out = [...tokens];
	for (let i = 0; i < out.length; i++) {
		if (!NEGATION_MARKERS.has(out[i])) continue;
		for (let j = i + 1; j <= i + 3 && j < out.length; j++) {
			const t = out[j];
			if (t === 'mrt' || t === 'mr' || t === '') continue; // Füllwort überspringen
			if (ALIAS_TOKENS.has(t)) out[j] = '';
			else break; // erstes nicht-region Wort beendet die negierte Phrase
		}
	}
	return out;
}

/**
 * Ordnet eine Praxis-Bezeichnung (ganze Zeile ODER nur das Label) allen
 * passenden Regionen zu. Liefert ein sortiertes Array (ggf. leer).
 */
export function classifyRegions(lineOrLabel: string): Region[] {
	const rawLabel = expandSpineAbbreviations(extractLabel(lineOrLabel));
	let norm = normalize(rawLabel);
	norm = stripWeightGender(norm);

	const isAngio = ANGIO_RE.test(norm);

	let tokens = norm.split(' ').filter(Boolean).map(lemmatize);
	tokens = applyNegations(tokens);
	if (isAngio) {
		tokens = tokens.filter((t, i, arr) => {
			if (VASCULAR_TERRITORY.has(t)) return false; // immer unterdrücken
			if (SOFT_TERRITORY.has(t)) {
				const prev = neighbour(arr, i, -1);
				const next = neighbour(arr, i, 1);
				if (prev.includes('angio') || next.includes('angio')) return false; // nur direkt an "angio"
			}
			return true;
		});
	}
	tokens = tokens.filter(Boolean);

	const found = new Set<Region>();
	for (const { region, tokens: needle } of ALIAS_ENTRIES) {
		if (containsSequence(tokens, needle)) found.add(region);
	}
	if (isAngio) found.add('angiographie');

	// "Hals-" als HWS-Abkürzung in Wirbelsäulen-Aufzählungen nicht als Hals werten.
	if (found.has('hals') && found.has('hws') && /\bhals\b[a-z ]*\b(bws|lws|brustwirbelsaeule|lendenwirbelsaeule)\b/.test(norm)) {
		found.delete('hals');
	}

	return REGIONS.filter((r) => found.has(r));
}

/** Convenience: voller Treffer inkl. Kontrastmittel-Achse. */
export interface MrtClassification {
	regions: Region[];
	kontrastmittel: Kontrastmittel;
}

export function classify(lineOrLabel: string): MrtClassification {
	return {
		regions: classifyRegions(lineOrLabel),
		kontrastmittel: extractKontrastmittel(normalize(extractLabel(lineOrLabel)))
	};
}

/* ──────────────────────────────────────────────────────────────────
 * 6) UI-Suche: Region-Auswahl per Freitext
 * ────────────────────────────────────────────────────────────────── */

/**
 * Filtert die Regionen für die Auswahl-UI anhand einer Freitext-Eingabe.
 * Gematcht wird gegen Anzeige-Label UND Aliase (Synonyme), token-basiert:
 * jedes Eingabe-Token muss als Teilstring im (normalisierten) Heuhaufen aus
 * Label + Aliasen vorkommen. Leere Eingabe → alle Regionen (in REGION-Reihenfolge).
 */
export function searchRegions(query: string): Region[] {
	const terms = normalize(query).split(' ').filter(Boolean);
	if (terms.length === 0) return [...REGIONS];
	return REGIONS.filter((region) => {
		const haystack = [REGION_META[region].label, ...ALIASES[region]].map(normalize).join(' ');
		return terms.every((t) => haystack.includes(t));
	});
}
